import { AdvancedMarker, useMap } from '@vis.gl/react-google-maps'
import { Add, Back, Close, MapSelect } from '@watersport/ui-react-icons'
import { Button } from '@watersport/ui-react-next'
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react'

import { useLayout } from 'contexts/LayoutProvider'
import { cn } from 'features/common/helpers/cn'
import { useModal } from 'features/common/hooks/useModal'
import ConfirmModal from 'features/locationMap/components/ConfirmModal'
import DrawInstruction from 'features/locationMap/components/DrawInstruction'
import Toast from 'features/locationMap/components/Toast'
import { useDrawingManager } from 'features/locationMap/hooks/useDrawingManager'
import { useDrawingManagerEvents } from 'features/locationMap/hooks/useDrawingManagerEvents'
import { useOverlaySnapshots } from 'features/locationMap/hooks/useOverlaySnapshots'
import reducer from 'features/locationMap/reducer'
import { DrawingActionKind } from 'features/locationMap/types'

export interface DrawingMapViewProps {
  polygons?: [number, number][][]
  goBack: () => void
  onChangeViewMode: (mode: 'select' | 'drawing' | undefined) => void
  onSearch: (polygons: [number, number][][]) => void
}

export interface DrawingMapViewInterface {
  setInitialPolygons: (polygons: [number, number][][]) => void
}

const DrawingMapView: React.ForwardRefRenderFunction<
  DrawingMapViewInterface,
  DrawingMapViewProps
> = ({ goBack, polygons, onChangeViewMode, onSearch }, ref) => {
  const [state, dispatch] = useReducer(reducer, { now: [] }, undefined)
  const [isDrawingNow, setIsDrawingNow] = useState<boolean>(false)
  const isFirstDrawing = useMemo(() => state.now.length < 1, [state.now.length])

  const map = useMap()
  const drawingManager = useDrawingManager()
  const overlaysShouldUpdateRef = useRef<boolean>(false)

  useDrawingManagerEvents(drawingManager, overlaysShouldUpdateRef, dispatch)
  const { topPoints } = useOverlaySnapshots(map, state, overlaysShouldUpdateRef)

  const enableDrawing = useCallback(
    (status: boolean) => {
      if (!status) {
        setIsDrawingNow(true)
        drawingManager?.setDrawingMode(google.maps.drawing.OverlayType.POLYGON)
      }
      setIsDrawingNow(status)
      drawingManager?.setDrawingMode(status ? google.maps.drawing.OverlayType.POLYGON : null)
    },
    [drawingManager],
  )

  useEffect(() => {
    enableDrawing(false)
  }, [enableDrawing, state.now.length])

  const { showInstruction, setShowInstruction } = useLayout()

  const searchButtonText = useMemo(() => {
    if (!isDrawingNow && state.now.length > 0) {
      return 'Search this area'
    }
    return 'Draw area'
  }, [isDrawingNow, state.now.length])

  const { show, showModal, closeModal, confirm } = useModal<boolean>()

  const removePolygon = useCallback(
    async (index: number) => {
      const result = await showModal()
      if (result) {
        dispatch({ type: DrawingActionKind.REMOVE_OVERLAY, payload: index })
      }
    },
    [dispatch, showModal],
  )

  const onClickSearch = useCallback(() => {
    const result = state.now.map(
      o => o.snapshot.path?.map(p => [p.lng(), p.lat()] as [number, number]) ?? [],
    )
    onSearch(result)
  }, [onSearch, state.now])

  const onClickDraw = useCallback(
    (status: boolean) => {
      setShowInstruction(false)
      enableDrawing(status)
    },
    [enableDrawing, setShowInstruction],
  )

  useImperativeHandle(
    ref,
    () => ({
      setInitialPolygons: (p: [number, number][][] | undefined) => {
        if (p) dispatch({ type: DrawingActionKind.SET_INITIAL_OVERLAYS, payload: p })
      },
    }),
    [],
  )

  useEffect(() => {
    if (polygons) {
      dispatch({ type: DrawingActionKind.SET_INITIAL_OVERLAYS, payload: polygons })
      map?.setCenter({ lat: polygons[0][0][1], lng: polygons[0][0][0] })
    }
  }, [map, polygons])

  return (
    <div className="" data-testid="drawing-map-view-container">
      {showInstruction && (
        <DrawInstruction onClose={() => setShowInstruction(false)} data-testid="draw-instruction" />
      )}
      {!isDrawingNow &&
        topPoints.map((p, index) => (
          <AdvancedMarker
            position={p}
            key={index}
            onClick={() => removePolygon(index)}
            data-testid={`polygon-marker-${index}`}
          >
            <div
              className="w-[34px] h-[34px] flex items-center justify-center bg-blue-600 rounded-lg z-50"
              data-testid={`polygon-remove-button-${index}`}
            >
              <Close className="text-white w-6 h-6" />
            </div>
          </AdvancedMarker>
        ))}
      <div
        className="absolute bottom-0 left-0 right-0 flex flex-col gap-4"
        data-testid="action-buttons-container"
      >
        <div className="px-5 z-20 w-1/2">
          <Button
            containerClassName="w-fit"
            className={cn('pr-5')}
            variant={'outline'}
            text={isDrawingNow ? 'Cancel drawing' : `Add ${isFirstDrawing ? 'first ' : ''}area`}
            leftIcon={
              isDrawingNow ? (
                <Close className="w-6 h-6 text-blue-700 m-[2px]" />
              ) : (
                <Add className="w-6 h-6 text-blue-700 m-[2px]" />
              )
            }
            size={'xl'}
            onClick={() => onClickDraw(!isDrawingNow)}
            data-testid="draw-toggle-button"
          />
        </div>
        <div
          className="bg-blue-200 rounded-t-3xl px-5 py-4 flex gap-4"
          data-testid="search-and-select-buttons"
        >
          <Button
            containerClassName="w-auto"
            className={cn(
              'pr-5 disabled:text-gray-500 disabled:bg-gray-100 disabled:border-gray-300 disabled:border',
            )}
            variant={'outline'}
            text={'Select'}
            leftIcon={
              <MapSelect
                className={cn('w-6 h-6 m-[2px] text-blue-700', isDrawingNow && 'text-gray-500')}
              />
            }
            rounded
            size={'xl'}
            onClick={() => onChangeViewMode('select')}
            active={!isDrawingNow}
            data-testid="select-mode-button"
          />
          <Button
            className={cn(
              'disabled:text-gray-500 disabled:bg-gray-100 disabled:border-gray-500 disabled:border',
            )}
            variant={'default'}
            text={searchButtonText}
            rounded
            size={'xl'}
            onClick={() => onClickSearch()}
            active={!isDrawingNow && state.now.length > 0}
            data-testid="search-button"
          />
        </div>
      </div>
      <Button
        containerClassName="w-[52px] h-[52px] absolute top-4 left-5"
        className="bg-blue-600 rounded-lg shadow-md cursor-pointer hover:bg-blue-600"
        variant={'default'}
        text={<Back width={24} height={24} />}
        onClick={goBack}
        data-testid="go-back-button"
      />
      {!showInstruction && <Toast message={'Mode: Draw area on the map'} />}
      <ConfirmModal show={show} onClose={closeModal} onConfirm={() => confirm(true)} />
    </div>
  )
}

export default React.forwardRef(DrawingMapView)
